import {
	Button,
	type ComboboxItem,
	Grid,
	Group,
	Input,
	LoadingOverlay,
	Modal,
	NumberInput,
	Select,
	Skeleton,
	Space,
	Text,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useDisclosure } from '@mantine/hooks';
import {
	IconBuildingStore,
	IconCalendar,
	IconCancel,
	IconCertificate,
	IconMoneybag,
} from '@tabler/icons-react';
import { useParams } from 'react-router-dom';
import {
	defaultIconStyle,
	inputIconStyle,
} from '../../../theme/styling/iconStyling';
import { mutate } from '../../../util/api/mutate';
import { query } from '../../../util/api/query';
import type { SelectableBranchDto } from '../../../util/types/SelectableBranchDto';
import { useNewWeeklyTargetForm } from '../hooks/useNewWeeklyTargetForm';
import type { NewWeeklyTargetDto } from '../types/WeeklyTargetDto';
import { Title } from '../../../components/Title';

interface OrganisationEditorModal {
	close: () => void;
	opened: boolean;
	triggerListUpdate: () => void;
}

export function NewWeeklyTarget({
	opened,
	close,
	triggerListUpdate,
}: OrganisationEditorModal) {
	const { organisation } = useParams();

	const [
		showSuccessModal,
		{ open: openSuccessModal, close: closeSuccessModal },
	] = useDisclosure(false);

	const { form, inputs } = useNewWeeklyTargetForm();

	const newOrganisationMutation = mutate<NewWeeklyTargetDto>(
		`${organisation}/weekly-targets/add`,
		inputs,
	);

	const pharmaciesQuery = query<SelectableBranchDto>(
		`${organisation}/branches/options`,
	);

	const resetAndClose = () => {
		form.reset();
		triggerListUpdate();
		close();
		closeSuccessModal();
	};

	return (
		<>
			<Modal
				opened={opened}
				onClose={close}
				title={<Title label={'Edit Target'} />}
				w={100}
				withCloseButton={true}
				size={'xs'}
				centered
			>
				<LoadingOverlay visible={newOrganisationMutation.isLoading} />
				<Modal
					opened={showSuccessModal}
					onClose={resetAndClose}
					title={<Title label={'Weekly Target Added!'} />}
					centered
				>
					<Text>
						A new weekly target has been successfully updated. Click Ok to
						continue...
					</Text>
					<Group justify={'flex-end'}>
						<Button
							color={'medepos.1'}
							variant={'filled'}
							leftSection={<IconCancel {...defaultIconStyle} />}
							onClick={() => resetAndClose()}
						>
							Ok
						</Button>
					</Group>
				</Modal>
				<Space h={6} />
				<form
					onSubmit={form.onSubmit(async () => {
						await newOrganisationMutation.mutateAsync().then((value) => {
							if (value.isValid) {
								openSuccessModal();
							}
						});
					})}
				>
					<Grid>
						<Grid.Col>
							<DatePickerInput
								clearable
								defaultValue={new Date()}
								label="Pick date"
								placeholder="Pick date"
								leftSection={<IconCalendar {...inputIconStyle} />}
								key={form.key('startDate')}
								{...form.getInputProps('startDate')}
								excludeDate={(date) => new Date(date).getDay() !== 1}
							/>
						</Grid.Col>
						<Grid.Col>
							<NumberInput
								label={'Target'}
								placeholder="Insert financial target..."
								prefix="£"
								decimalScale={2}
								thousandSeparator=","
								allowDecimal={true}
								allowNegative={false}
								key={form.key('target')}
								{...form.getInputProps('target')}
								leftSection={<IconMoneybag {...inputIconStyle} />}
							/>
						</Grid.Col>
						<Grid.Col>
							{!pharmaciesQuery.isLoading ? (
								<Select
									label={'Branch'}
									placeholder="Select branch associated with target..."
									data={pharmaciesQuery.data?.value.branches.flatMap((p) => {
										const comboBoxItem: ComboboxItem = {
											label: `${p.area} - ${p.name}`,
											value: p.id.toString(),
										};

										return comboBoxItem;
									})}
									searchable
									clearable
									comboboxProps={{
										transitionProps: { transition: 'pop', duration: 200 },
									}}
									key={form.key('branchId')}
									{...form.getInputProps('branchId')}
									data-cy="branchId-input"
									leftSection={<IconBuildingStore {...inputIconStyle} />}
								/>
							) : (
								<Input.Wrapper label={'Branch'}>
									<Skeleton
										height={8}
										mt={6}
										width="100%"
										radius={'xl'}
										h={25}
									/>
								</Input.Wrapper>
							)}
						</Grid.Col>
					</Grid>
					<Space h={20} />
					<Group justify={'flex-end'}>
						<Button
							color={'medepos.1'}
							variant={'filled'}
							leftSection={<IconCancel {...defaultIconStyle} />}
							onClick={() => close()}
						>
							Close
						</Button>
						<Button
							color={'medepos.1'}
							variant={'filled'}
							leftSection={<IconCertificate {...defaultIconStyle} />}
							type={'submit'}
						>
							New Target
						</Button>
					</Group>
				</form>
			</Modal>
		</>
	);
}
